import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Copy, Download, FileUp, Loader2, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { API, adminApi, czk, errMsg, fmtSize, media } from "../lib/api";
import { useSite } from "../lib/store";
import { Field, MultiImages, PageTitle, Toggle, uploadChunked, useList } from "./ui";

const EMPTY = { name: "", slug: "", short_desc: "", description: "", price: "", sale_price: "", stock: "", category_ids: [], images: [], files: [], is_new: true, show_on_home: false, active: true, age: "3–7 let", format: "PDF", pages: "", order: 0 };

const FilesEditor = ({ value, onChange, onAppend }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [prog, setProg] = useState("");
  const pick = async (e) => {
    const files = [...(e.target.files || [])];
    setBusy(true);
    const out = [];
    try {
      for (const [n, f] of files.entries()) {
        const r = await uploadChunked(f, (pct) => setProg(`${f.name} (${n + 1}/${files.length}) – ${pct} %`));
        out.push({ id: r.id, name: r.name, path: r.path, size: r.size, content_type: r.content_type });
      }
      toast.success("Soubory nahrány – nezapomeňte produkt uložit");
    } catch (err) {
      toast.error(`Nahrávání selhalo: ${errMsg(err)}`);
    }
    if (out.length) onAppend(out);
    setBusy(false);
    setProg("");
    e.target.value = "";
  };
  const download = async (f) => {
    try {
      const { data } = await adminApi.post("/files/link", { path: f.path, name: f.name });
      window.location.href = `${API.replace(/\/api$/, "")}${data.url}`;
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <div className="space-y-2">
      {value.map((f, i) => (
        <div key={f.id} className="flex items-center gap-2 rounded-2xl bg-[#F7F3EA] p-2" data-testid={`product-file-${i}`}>
          <input value={f.name} onChange={(e) => onChange(value.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))} className="flex-1 rounded-xl bg-white px-3 py-2 text-sm outline-none" data-testid={`product-file-name-${i}`} />
          <span className="text-xs text-[#6E7565]">{fmtSize(f.size)}</span>
          <button type="button" onClick={() => download(f)} className="p-2 text-[#7A8460]" title="Stáhnout" data-testid={`product-file-download-${i}`}><Download className="h-4 w-4" /></button>
          <button type="button" onClick={() => onChange(value.filter((_, k) => k !== i))} className="p-2 text-[#D96B43]" data-testid={`product-file-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      {busy && prog && (
        <div className="rounded-2xl bg-white p-3 text-xs" data-testid="upload-progress">
          <p className="mb-1 truncate">Nahrávám {prog}</p>
          <div className="h-2 overflow-hidden rounded-full bg-[#E9EFE0]"><div className="h-full rounded-full bg-[#7A8460] transition-all" style={{ width: `${prog.match(/(\d+) %$/)?.[1] || 0}%` }} /></div>
        </div>
      )}
      <button type="button" disabled={busy} onClick={() => ref.current.click()} className="btn-ghost w-full" data-testid="product-files-upload">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileUp className="h-4 w-4" />} Nahrát soubory ke stažení (PDF, ZIP… i 250 MB)</button>
      <input ref={ref} type="file" multiple hidden onChange={pick} />
    </div>
  );
};

const Editor = ({ initial, onClose, onSaved }) => {
  const { site } = useSite();
  const [p, setP] = useState({ ...EMPTY, ...initial, price: initial.price ?? "", sale_price: initial.sale_price ?? "", stock: initial.stock ?? "", pages: initial.pages ?? "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setP({ ...p, [k]: e?.target ? e.target.value : e });
  const cats = site?.categories || [];
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (p.id) await adminApi.put(`/products/${p.id}`, p);
      else await adminApi.post("/products", p);
      toast.success("Produkt uložen");
      onSaved();
    } catch (err) {
      toast.error(errMsg(err));
    }
    setBusy(false);
  };
  const toggleCat = (id) => setP({ ...p, category_ids: p.category_ids.includes(id) ? p.category_ids.filter((x) => x !== id) : [...p.category_ids, id] });
  return (
    <motion.div className="fixed inset-0 z-50 flex justify-end bg-black/40" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      <motion.form onSubmit={save} initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }} transition={{ type: "spring", damping: 26 }} className="flex h-full w-full max-w-3xl flex-col bg-[#F7F3EA]" data-testid="product-editor">
        <div className="flex items-center justify-between border-b bg-white p-5">
          <h2 className="h-section text-2xl">{p.id ? "Upravit produkt" : "Nový produkt"}</h2>
          <button type="button" onClick={onClose} className="rounded-full bg-[#F7F3EA] p-2" data-testid="product-editor-close"><X /></button>
        </div>
        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Název *" required value={p.name} onChange={set("name")} testid="product-name-input" />
            <Field label="URL (slug)" value={p.slug} onChange={set("slug")} placeholder="automaticky z názvu" testid="product-slug-input" />
          </div>
          <Field label="Krátký popis" value={p.short_desc} onChange={set("short_desc")} testid="product-short-input" />
          <Field label="Popis produktu" textarea value={p.description} onChange={set("description")} testid="product-desc-input" />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Field label="Cena (Kč) *" type="number" step="0.01" min="0" required value={p.price} onChange={set("price")} testid="product-price-input" />
            <Field label="Akční cena" type="number" step="0.01" min="0" value={p.sale_price} onChange={set("sale_price")} testid="product-sale-input" />
            <Field label="Počet kusů" type="number" min="0" value={p.stock} onChange={set("stock")} placeholder="neomezeně" testid="product-stock-input" />
            <Field label="Pořadí" type="number" value={p.order} onChange={set("order")} testid="product-order-input" />
            <Field label="Věk" value={p.age} onChange={set("age")} testid="product-age-input" />
            <Field label="Formát" value={p.format} onChange={set("format")} testid="product-format-input" />
            <Field label="Počet stran" value={p.pages} onChange={set("pages")} testid="product-pages-input" />
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#6E7565]">Kategorie</p>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => <button type="button" key={c.id} onClick={() => toggleCat(c.id)} data-testid={`product-cat-${c.slug}`} className={`rounded-full border px-4 py-1.5 text-sm ${p.category_ids.includes(c.id) ? "border-[#7A8460] bg-[#7A8460] text-white" : "bg-white"}`}>{c.name}</button>)}
            </div>
          </div>
          <div className="flex flex-wrap gap-6 rounded-2xl bg-white p-4">
            <Toggle label="Aktivní (viditelný)" checked={p.active} onChange={set("active")} testid="product-active-toggle" />
            <Toggle label="Na hlavní stránce" checked={p.show_on_home} onChange={set("show_on_home")} testid="product-home-toggle" />
            <Toggle label="Štítek Novinka" checked={p.is_new} onChange={set("is_new")} testid="product-new-toggle" />
          </div>
          <div><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#6E7565]">Fotografie (první = hlavní)</p><MultiImages value={p.images} onChange={(v) => setP((prev) => ({ ...prev, images: typeof v === "function" ? v(prev.images) : v }))} /></div>
          <div><p className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#6E7565]">Soubory pro zákazníky (zpřístupní se po zaplacení)</p><FilesEditor value={p.files} onChange={set("files")} onAppend={(out) => setP((prev) => ({ ...prev, files: [...prev.files, ...out] }))} /></div>
        </div>
        <div className="flex gap-3 border-t bg-white p-5">
          <button type="button" onClick={onClose} className="btn-ghost">Zrušit</button>
          <button className="btn-sage flex-1" disabled={busy} data-testid="product-save-btn">{busy ? "Ukládám…" : "Uložit produkt"}</button>
        </div>
      </motion.form>
    </motion.div>
  );
};

export default function Products() {
  const [list, load] = useList("/products");
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const del = async (p) => {
    if (!window.confirm(`Smazat produkt „${p.name}“?`)) return;
    await adminApi.delete(`/products/${p.id}`);
    toast.success("Smazáno");
    load();
  };
  const dup = async (p) => {
    const { id, slug, ...rest } = p;
    await adminApi.post("/products", { ...rest, name: `${p.name} (kopie)`, active: false });
    load();
  };
  const quick = async (p, k) => {
    await adminApi.put(`/products/${p.id}`, { [k]: !p[k] });
    load();
  };
  const shown = (list || []).filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="admin-products">
      <PageTitle title="Produkty" sub={`${list?.length || 0} produktů`} action={<button className="btn-sage" onClick={() => setEdit({})} data-testid="product-add-btn"><Plus className="h-4 w-4" /> Nový produkt</button>} />
      <div className="mb-4 flex items-center gap-2 rounded-full border bg-white px-4 py-2.5 sm:max-w-sm"><Search className="h-4 w-4" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Hledat produkt" className="flex-1 bg-transparent text-sm outline-none" data-testid="admin-product-search" /></div>
      <div className="overflow-x-auto rounded-3xl border bg-white">
        <table className="w-full min-w-[760px] text-sm">
          <thead className="bg-[#F7F3EA] text-left text-xs uppercase tracking-wider text-[#6E7565]"><tr><th className="p-3">Produkt</th><th>Cena</th><th>Sklad</th><th>Prodáno</th><th>Soubory</th><th>Aktivní</th><th>Hlavní str.</th><th /></tr></thead>
          <tbody className="divide-y">
            {shown.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.slug}`}>
                <td className="p-3"><div className="flex items-center gap-3"><img src={media(p.images?.[0])} alt="" className="h-12 w-12 rounded-xl bg-[#F7F3EA] object-cover" /><span className="font-medium">{p.name}</span></div></td>
                <td>{p.sale_price ? <><b className="text-[#D96B43]">{czk(p.sale_price)}</b> <s className="text-xs">{czk(p.price)}</s></> : czk(p.price)}</td>
                <td>{p.stock ?? "∞"}</td><td>{p.sold || 0}</td><td>{p.files?.length || 0}</td>
                <td><Toggle checked={p.active} onChange={() => quick(p, "active")} testid={`quick-active-${p.slug}`} /></td>
                <td><Toggle checked={p.show_on_home} onChange={() => quick(p, "show_on_home")} testid={`quick-home-${p.slug}`} /></td>
                <td className="whitespace-nowrap pr-3 text-right">
                  <button onClick={() => setEdit(p)} className="rounded-full p-2 hover:bg-[#E9EFE0]" data-testid={`product-edit-${p.slug}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => dup(p)} className="rounded-full p-2 hover:bg-[#E9EFE0]" title="Duplikovat"><Copy className="h-4 w-4" /></button>
                  <button onClick={() => del(p)} className="rounded-full p-2 text-[#D96B43] hover:bg-[#F8E1D7]" data-testid={`product-delete-${p.slug}`}><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AnimatePresence>{edit && <Editor initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}</AnimatePresence>
    </div>
  );
}
